/**
 * How a ring group is walked: which of its children are cards, where a ring rests, and what a
 * selection fades.
 *
 * An image group (`AddImages.set`) holds two kinds of thing: the artwork cards, and the film
 * strip, which `addFilmStrip` adds *before* the cards are loaded — see Utils/filmStrip for why it
 * depends on nothing. So every walk over `imgGroup.children` has to say what a ring child is, and
 * each one used to say it differently:
 *
 *   * `resetImagePositions` and `rotateRing` filter on `userData.isImage`;
 *   * `restRing` did not filter at all, and wrote `scale.setScalar(child.userData.scale)` on every
 *     child — the band included, which has no `userData.scale`. `setScalar(undefined)` leaves the
 *     scale `(undefined, undefined, undefined)`, three composes a matrix of NaN out of that, and a
 *     NaN matrix is frustum-culled: the strip vanished the first time the pointer touched a card
 *     on its ring, and stayed gone, because nothing writes the band's scale again;
 *   * the two selection traverses in MainScene classified by a blacklist — *anything that is not
 *     an artwork is the planet's own furniture* — which is a set the strip is not in. That put it
 *     with the planets and labels to be brought back up, so **leaving any planet lit the strip on
 *     every other one**, and only that one planet's own strip went down.
 *
 * Both decisions are pure and live here, where a test can reach them. MainScene reads them.
 */

/** A card, as `AddImages.create` marks one. The band, the group, and everything else are not. */
export function isRingCard(object) {
    return !!object.userData.isImage;
}

/** The film strip, as Utils/filmStrip marks it. */
export function isFilmStrip(object) {
    return !!object.userData.isFilmStrip;
}

/**
 * Every card on a ring back to the size it is placed at.
 *
 * Cards only. A ring's other children have no `userData.scale` — the size a card is drawn at is
 * this module's business, and nothing else's — so writing one into the band is what made the
 * strip disappear. See the module comment.
 */
export function restRing(children) {
    children.forEach(child => {
        if (!isRingCard(child)) return;

        child.scale.setScalar(child.userData.scale);
    });
}

/**
 * The opacity a selection gives `object`, or `null` to leave it as it is.
 *
 * `planetName` is the planet the selection is on; `focused` is whether the camera has arrived on
 * it (`selectPlanet`) or left it (`deselectPlanet`). The two traverses are otherwise the same
 * walk, which is why one function answers for both — and why the one cell they used to disagree
 * about is visible here rather than buried in a branch.
 *
 * The band answers for itself on both sides. It is the one child of a ring whose *absence* is not
 * already implied by the branch it is in: another planet's cards and furniture are left at 0 by
 * their own selection, but another planet's strip was matched by the blacklist above and brought
 * back up.
 */
export function ringOpacity(object, { planetName, focused }) {
    const mine = object.userData.group === planetName;

    if (isRingCard(object)) {
        // This ring's cards and no others. Another planet's cards belong to whatever its own
        // selection last gave them, which is opacity 0 on layer 1.
        return mine ? (focused ? 1 : 0) : null;
    }

    if (isFilmStrip(object)) {
        // Up with its own ring, and down on every other planet — whether or not one of those has
        // ever been visited.
        return mine && focused ? 1 : 0;
    }

    // The planet's own mesh, its label, its ring: the furniture a selection clears away and puts
    // back. The camera's own planet is left alone — the selection already has it.
    return mine ? null : (focused ? 0 : 1);
}

/**
 * The planet the last selection was made on, or `null` for nobody's.
 *
 * The two traverses above are the only writers of a ring child's opacity anywhere, so a child
 * that joins the ring *after* a traverse has been and gone misses the write entirely: `create`
 * and `addFilmStrip` both put their mesh in at 0 and nothing revisits it. A card that arrives
 * while its planet is selected is then invisible for as long as the selection lasts — a ring
 * missing most of its artwork — and the band is the worse half, because `addFilmStrip` only runs
 * once the ring's *last* texture has landed, which on this gallery's sizes is routinely after the
 * click: the strip is built, and never appears. Leaving the planet and coming back fades
 * everything in, which is why it reads as intermittent.
 *
 * So the traverses record where they left the camera and a newborn asks. The name is kept beside
 * the decision it is an argument to, rather than in MainScene, because "what a selection fades"
 * is this module's question.
 */
let focusedPlanet = null;

/**
 * Where the camera is, for as long as the selection that put it there lasts.
 *
 * Called by `selectPlanet` with its planet and by `deselectPlanet` with null — the same two
 * moments the traverses run, so what `ringBirth` reads is always what the last traverse wrote.
 */
export function setFocusedPlanet(planetName) {
    focusedPlanet = planetName || null;
}

/**
 * The same name back out, for a reader that is not deciding what to fade.
 *
 * The category row (`3DWebpage/NavBar`) marks the button for wherever the camera is, and its
 * answer has to be the same one the traverses use rather than a second copy of it kept in step
 * by hand — the row and the rings would then be able to disagree about which planet is current,
 * which is exactly the class of bug this module was extracted to end.
 */
export function getFocusedPlanet() {
    return focusedPlanet;
}

/**
 * Puts a ring child that has just joined the scene where the current selection would have put it.
 *
 * The same call the traverses make, with `focused` true, against the selection they last made it
 * for — so a card or a band that arrives late is indistinguishable from one that was already
 * there. A card on the selected ring also goes to layer 0, where `selectPlanet` puts that ring's
 * cards and where all three raycasters can reach them: born on layer 1 it would be lit but
 * unclickable until the ring was left and entered again.
 *
 * Only a ring child — a card or the strip. `create` and `addFilmStrip` are the callers.
 */
export function ringBirth(object) {
    const opacity = ringOpacity(object, { planetName: focusedPlanet, focused: true });

    if (opacity !== null) object.material.opacity = opacity;

    if (isRingCard(object) && object.userData.group === focusedPlanet) object.layers.set(0);
}
